import { z } from 'zod';
import { TIPOS_PREGUNTA } from './cuestionarios';

/**
 * M8 — los resultados agregados.
 *
 * ── Qué forma tiene un resultado, y por qué esta ──
 * **La distribución de respuestas, no un puntaje.** Hoy Copathy no puntúa
 * (D-027): el resultado es cuántas personas eligieron cada cosa. Un promedio
 * exigiría decidir cuánto vale cada punto de cada escala, y eso es una decisión
 * de método que Copathy no ha tomado — inventarla en el código sería inventarle
 * el instrumento al cliente.
 *
 * ── Lo que estos tipos NO deciden ──
 * Quién ve qué. Eso son dos niveles que se multiplican (D-114): el **permiso**
 * del rol, que comprueba la API, y la **configuración de esa empresa**, que
 * comprueban las funciones del motor. El contrato solo describe la forma; el
 * mismo endpoint devuelve más o menos según quién pregunte, y por eso casi todo
 * acá es opcional o anulable.
 */

/** Un valor contado: un punto de escala, una opción, un «sí». */
export const esquemaCelda = z.object({
  /** Del ítem de escala u opción. `null` en sí/no y en las de un solo conteo. */
  valorId: z.number().int().positive().nullable(),
  /** Para ordenar: el orden del punto de escala o de la opción. */
  valorOrden: z.number().int(),
  etiqueta: z.string(),
  conteo: z.number().int().nonnegative(),
});
export type Celda = z.infer<typeof esquemaCelda>;

/** Una fila de una matriz: la afirmación con su propio reparto. */
export const esquemaFilaMatriz = z.object({
  itemId: z.number().int().positive(),
  itemOrden: z.number().int(),
  texto: z.string(),
  celdas: z.array(esquemaCelda),
});
export type FilaMatriz = z.infer<typeof esquemaFilaMatriz>;

/**
 * El resultado de una pregunta.
 *
 * `celdas` y `filas` son excluyentes: una matriz reparte por afirmación y todo lo
 * demás reparte directo. Se modelan como dos campos opcionales en vez de una
 * unión porque la interfaz decide qué dibujar por `tipo`, que ya viene, y una
 * unión discriminada obligaría a repetir esa decisión en el contrato.
 */
export const esquemaPreguntaResultado = z.object({
  preguntaId: z.number().int().positive(),
  orden: z.number().int(),
  texto: z.string(),
  tipo: z.enum(TIPOS_PREGUNTA),
  competencia: z.string().nullable(),
  /** Todo menos las matrices. */
  celdas: z.array(esquemaCelda).optional(),
  /** Solo las matrices. */
  filas: z.array(esquemaFilaMatriz).optional(),
  /**
   * `true` en las preguntas de texto libre: lo único que trae la distribución es
   * **cuántas personas escribieron algo**, nunca lo que escribieron. El contenido
   * se pide aparte —`/textos`— y depende del permiso y de la configuración del
   * cliente (D-114).
   */
  esTexto: z.boolean(),
  /**
   * `true` si la pregunta ofrecía una **observación abierta** (035), aunque ella
   * misma no sea de texto: una likert con «¿por qué?» debajo.
   *
   * ⚠️ Lo que se escribió ahí **se pide por la misma puerta que los textos** —
   * `/textos`, con `resultados.ver_textos`, la configuración del cliente y el
   * mínimo por grupo (D-114)—. Es texto libre sobre el propio jefe: si saliera
   * por el conteo de opciones, la frontera 3 se rompería sin que fallara nada.
   *
   * Se manda aparte de `esTexto` y no mezclado con él porque la distribución de
   * la pregunta **sí existe** —los cinco puntos de la likert siguen contándose—
   * y la pantalla tiene que dibujar las dos cosas: el reparto y, debajo, el
   * acceso a los comentarios.
   */
  tieneObservaciones: z.boolean(),
});
export type PreguntaResultado = z.infer<typeof esquemaPreguntaResultado>;

/**
 * El informe de una aplicación.
 *
 * ⚠️ **`entregable: false` no es un error, y hay que dibujarlo distinto de «no hay
 * respuestas».** Significa que el grupo no alcanza el mínimo configurado para esa
 * empresa, así que no se entrega el detalle. Son dos situaciones diferentes y
 * confundirlas hace que alguien crea que su gente no respondió.
 *
 * Y por eso `total` es anulable: cuando no alcanza, **también se oculta el
 * total**. Decir «hay 2 respuestas» en un área de 3 personas ya dice demasiado.
 */
export const esquemaResultados = z.object({
  aplicacionId: z.number().int().positive(),
  titulo: z.string(),
  momento: z.string(),
  cuestionarioNombre: z.string(),
  version: z.number().int().positive(),
  empresaNombre: z.string(),
  /** `null` cuando el grupo no alcanza el mínimo. */
  total: z.number().int().nonnegative().nullable(),
  minimo: z.number().int().positive(),
  entregable: z.boolean(),
  /** El corte aplicado, para que la interfaz sepa qué está mostrando. */
  area: z.string().nullable(),
  cargo: z.string().nullable(),
  /** Vacío cuando `entregable` es `false`. */
  preguntas: z.array(esquemaPreguntaResultado),
});
export type Resultados = z.infer<typeof esquemaResultados>;

/**
 * Los cortes con respuestas suficientes.
 *
 * Sirve para armar el selector, y no es un lujo: **sin esto el selector se
 * llenaría con el catálogo completo de áreas**, y elegir una vacía daría un
 * informe en blanco que se lee como un error.
 *
 * Los grupos por debajo del mínimo **no aparecen** para quien no cruza la
 * frontera. Listarlos con su conteo entregaría por la ventana lo que el informe
 * tapa.
 */
export const esquemaCorte = z.object({
  clase: z.enum(['area', 'cargo']),
  valor: z.string(),
  conteo: z.number().int().positive(),
});
export type Corte = z.infer<typeof esquemaCorte>;

export const esquemaFiltroResultados = z.object({
  area: z.string().trim().min(1).max(120).optional(),
  cargo: z.string().trim().min(1).max(120).optional(),
});
export type FiltroResultados = z.infer<typeof esquemaFiltroResultados>;

/**
 * Las respuestas de texto libre de una pregunta, sin quién las escribió.
 *
 * ⚠️ Lo más delicado de M8. Un texto sobre el propio jefe **es identificable por
 * su contenido** en un equipo pequeño, aunque no lleve nombre — quitar el nombre
 * no anonimiza «mi jefe me grita» escrito por una de cinco personas. Por eso hace
 * falta el permiso `resultados.ver_textos` **y** que la empresa lo tenga
 * habilitado, y por eso lo segundo arranca apagado (D-114).
 *
 * `habilitado: false` significa «esa empresa no recibe textos», que es distinto de
 * «nadie escribió»: la interfaz lo dice distinto o miente.
 */
export const esquemaTextos = z.object({
  preguntaId: z.number().int().positive(),
  texto: z.string(),
  habilitado: z.boolean(),
  respuestas: z.array(z.string()),
});
export type TextosResultado = z.infer<typeof esquemaTextos>;
